import React, { useMemo, useState, useEffect } from 'react';
import {
    useReactTable,
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,SortingState
} from '@tanstack/react-table';
import api from '../../config/axios';
import AdminMenu from '../../components/admin/menu';

interface TClub {
    clubId: number;
    clubLib: string;
    valueLink: string;
    clubTel: string;
    clubAdresse: string;
    clubMail: string;
}

const AdminClub: React.FC = () => {
    const [data, setData] = useState<TClub[]>([]);
    const [globalFilter, setGlobalFilter] = useState('');        
    const [sorting, setSorting] = useState<SortingState>([]);

    const handleDelete = async (id: number) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce club ?')) {
            try {
                await api.delete(`/club/${id}`);
                setData(data.filter(club => club.clubId !== id));
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
            }
        }
    };

    useEffect(() => {
        const fetchClubs = async () => {
            try {
                const response = await api.get('/club');
                setData(response.data);
            } catch (error) {
                console.error('Erreur lors du chargement des clubs:', error);
            }
        };
        fetchClubs();
    }, []);

    const columnHelper = createColumnHelper<TClub>();
    const columns = useMemo(
        () => [
            columnHelper.accessor('clubId', {
                header: () => 'ID',
                cell: info => info.getValue(),
            }),
            columnHelper.accessor('clubLib', {
                header: () => 'Nom',
                cell: info => info.getValue(),
            }),
            columnHelper.accessor('clubTel', {
                header: () => 'Téléphone',
                cell: info => info.getValue(),
            }),
            columnHelper.accessor('clubAdresse', {
                header: () => 'Adresse',
                cell: info => info.getValue(),
            }),
            columnHelper.accessor('clubMail', {
                header: () => 'Email',
                cell: info => info.getValue(),
            }),
            columnHelper.accessor('valueLink', {
                header: () => 'Value Link',
                cell: info => info.getValue(),
            }),
            columnHelper.display({
                id: 'actions',
                header: () => 'Actions',
                cell: ({ row }) => (
                    <div className="flex space-x-2">
                        <button
                            onClick={() => alert(`Modifier ID: ${row.original.clubId}`)}
                            className="bg-yellow-500 text-white px-2 py-1 rounded-md hover:bg-yellow-400"
                        >
                            Modifier
                        </button>
                        <button
                            onClick={() => handleDelete(row.original.clubId)}
                            className="bg-red-600 text-white px-2 py-1 rounded-md hover:bg-red-500"
                        >
                            Supprimer
                        </button>
                    </div>
                ),
            }),
        ],
        []
    );

    const table = useReactTable({
        data,
        columns,
        state: {
            globalFilter,
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    return (
        <>
            <div className="flex h-screen">
                <AdminMenu />
                <main className="flex-1 p-6 bg-white shadow-md rounded-md">
                    <h1 className="text-2xl font-bold mb-4">Gestion des Clubs</h1>
                    {/* Filtrage global */}
                    <input
                        type="text"
                        value={globalFilter || ''}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Rechercher..."
                        className="mb-4 p-2 border rounded-md"
                    />

                    {/* Table */}
                    <table className="table-auto w-full border-collapse border border-gray-200">
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th key={header.id} className="border px-4 py-2 bg-gray-100">
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {/* Ajout de tri */}
                                            {header.column.getCanSort() && (
                                                <button
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    className="ml-2 text-blue-500"
                                                >
                                                    {header.column.getIsSorted() === 'asc' ? ' 🔼' : header.column.getIsSorted() === 'desc' ? ' 🔽' : '⇅'}
                                                </button>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="border px-4 py-2">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            {table.getFooterGroups().map((footerGroup) => (
                                <tr key={footerGroup.id}>
                                    {footerGroup.headers.map((header) => (
                                        <td key={header.id} className="border px-4 py-2">
                                            {flexRender(header.column.columnDef.footer, header.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tfoot>
                    </table>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4">
                        <button
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                            className="bg-gray-200 px-4 py-2 rounded-md disabled:opacity-50"
                        >
                            Précédent
                        </button>
                        <span>
                            Page{' '}
                            <strong>
                                {table.getState().pagination.pageIndex + 1} sur {table.getPageCount()}
                            </strong>
                        </span>
                        <button
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                            className="bg-gray-200 px-4 py-2 rounded-md disabled:opacity-50"
                        >
                            Suivant
                        </button>
                        <select
                        value={table.getState().pagination.pageSize}
                        onChange={e => table.setPageSize(Number(e.target.value))}
                        className="px-3 py-1 border rounded"
                    >
                        {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                Afficher {pageSize}
                            </option>
                        ))}
                    </select>
                    </div>
                </main>
            </div>
        </>
    );
};

export default AdminClub;